import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import styled from 'styled-components';

import { renderFormField, getOptions } from 'modules/form';
import {
  studentTransferClassesFields,
  studentTransferPartialClassesFields,
  teacherStates,
  MENU_CLOSE,
  ERROR_MSG_FOR_TEACHER_LIST,
  updateFieldChoices,
} from 'constants/form';
import { Button, Row, Separator, Text } from '@cuemath/comps';
import BallLoader from 'comps/ball-loader';
import { Message } from 'modules/form';
import { checkButtonStatus } from 'utils';
import { getAll as getTeacherList } from 'actions/teacher-list';

import { TEACHERS_PER_PAGE } from 'constants/list';
import {
  get as getStudentCourseClassesTranferDetails,
  patch as patchStudentCourseClassesTranferDetails,
} from 'actions/student-course-classes-transfer';
import { closeModal } from 'actions/modal';
import { invalidateStudentCoursesGetAll } from 'actions/studentsv2';

const Wrapper = styled.div`
  padding: 40px 10px 20px;
`;

const ButtonWrapper = styled(Row)`
  justify-content: center;
`;

const ConfirmationText = styled(Text)`
  font-size: 14px;
  color: #333;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
`;

function getFormDefaults(studentTransferClassesFieldsFiltered) {
  const formDefaults = {};

  studentTransferClassesFieldsFiltered.forEach(field => {
    formDefaults[field.name] = field.default;
  });
  return formDefaults;
}

const StudentTransferClassesForm = ({
  studentCourse,
  actions,
  studentCourseClassesTransfer,
  isPartialTransfer,
  courseListData,
  studentId,
  teacherList,
}) => {
  const studentTransferClassesFieldsFiltered = isPartialTransfer
    ? studentTransferPartialClassesFields
    : studentTransferClassesFields;
  const [form, setForm] = useState(
    getFormDefaults(studentTransferClassesFieldsFiltered) || [],
  );
  const [fields, setFields] = useState(studentTransferClassesFieldsFiltered || []);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [teacherSearchKey, setTeacherSearchKey] = useState(null);
  const [patchData] = useState(store.patch());
  const { id: patchId } = patchData || {};
  const patchStudentCourseClassesTransfer = store.patch(
    studentCourseClassesTransfer,
    patchId,
  );
  const { resolved: patchResolved } = patchStudentCourseClassesTransfer || {};
  const { data: patchStudentCourseClassesTransferData, isPatched, isPatchingFailed } =
    patchResolved || {};
  const patchErrorObj = patchResolved && store.getError(patchResolved);
  const { message: patchErrorMsg } = patchErrorObj || {};

  const { id: studentCourseId } = studentCourse || {};

  const studentCourseClassesTransferData = store.get(
    studentCourseClassesTransfer,
    studentCourseId,
  );

  const { data: studentCourseClassesTransferDetails, isLoaded, isLoadingFailed } =
    studentCourseClassesTransferData || {};
  const {
    pending_classes: pendingClasses,
    unserviced_amount: unservicedAmount,
    students,
    online_band_list: onlineBandList,
    target_enrollment_type: targetEnrollmentType,
    target_class_ratio: targetClassRatio,
  } = studentCourseClassesTransferDetails || {};
  const getErrorObj =
    studentCourseClassesTransferData && store.getError(studentCourseClassesTransferData);
  const { message: getErrorMsg } = getErrorObj || {};

  const fillForm = () => {
    let newForm = {};

    newForm.unserviced_amount = unservicedAmount.toString();
    newForm.pending_classes = pendingClasses.toString();

    setForm({ ...form, ...newForm });
  };

  const timerRef = useRef(null);

  const onCloseModal = useCallback(() => {
    timerRef.current = setTimeout(() => {
      actions.closeModal();
      if (studentId) {
        invalidateStudentCoursesGetAll(studentId);
      }
    }, 1500);
  }, [actions, studentId]);
  const listParams = { page: 1, per_page: TEACHERS_PER_PAGE, search: teacherSearchKey };
  const teachersData = store.getAll(teacherList, listParams);
  const renderTeachersList = () => {
    const { data } = teachersData || {};

    const teachersDataAvailable = data && Array.isArray(data) && data.length > 0;

    if (teachersDataAvailable) {
      const choicesListOfTeachers = getOptions({
        field: 'teachers_list',
        teachersDataOptions: teachersData.data,
      });

      updateFieldChoices(['teacher_id'], fields, choicesListOfTeachers);
    }
  };

  const onTeacherSearch = (val, reason) => {
    const { action } = reason;
    const { EXCLUDE_INACT_INTR } = teacherStates;

    const listParams = { page: 1, per_page: TEACHERS_PER_PAGE, search: val };
    const filterData = {
      page: 1,
      per_page: TEACHERS_PER_PAGE,
      state: EXCLUDE_INACT_INTR,
      search: val,
    };

    if (val && val.length <= 4 && action !== MENU_CLOSE) {
      setErrorText(ERROR_MSG_FOR_TEACHER_LIST);
      return;
    }
    if (errorText) {
      setErrorText(null);
    }

    if (action !== MENU_CLOSE || val.length !== 0) {
      setTeacherSearchKey(val);
      actions.getTeacherList(listParams, filterData);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (studentCourseId) {
      actions.getStudentCourseClassesTranferDetails(studentCourseId);
      setLoading(true);
    }
  }, []);

  useEffect(() => {
    if (isLoaded) {
      fillFields(studentCourseClassesTransferDetails);
      fillForm(studentCourseClassesTransferDetails);
      setLoading(false);
    } else if (isLoadingFailed) {
      setError(getErrorMsg);
      setLoading(false);
    }
  }, [isLoaded, isLoadingFailed]);

  useEffect(() => {
    if (isPatched) {
      setSuccess(patchStudentCourseClassesTransferData);
      setLoading(false);
      onCloseModal();
    } else if (isPatchingFailed) {
      setError(patchErrorMsg);
      setLoading(false);
    }
  }, [isPatched, isPatchingFailed]);

  function fillFields() {
    const newFields =
      fields &&
      fields.map(field => {
        if (pendingClasses === 0) {
          return { ...field, disabled: true };
        }
        if (field.name === 'students' && students) {
          let choices = [];
          Object.entries(students).forEach(([studentId, studentData]) => {
            const { first_name: firstName } = studentData || {};
            choices.push({ label: firstName, value: studentId });
          });
          return { ...field, choices, disabled: false };
        }
        if (field.name === 'online_band_list' && onlineBandList) {
          let modifiedChoices = [];
          onlineBandList.forEach(band => {
            const { id: onlineBandId, name } = band || {};
            modifiedChoices.push({ label: name, value: onlineBandId });
          });
          return { ...field, choices: modifiedChoices, disabled: false };
        }
        if (field.name === 'course' && isPartialTransfer) {
          let choices = [];
          courseListData &&
            courseListData?.data &&
            courseListData?.data?.forEach(course => {
              const { name: courseName } = course || {};
              const label = courseName;
              const value = course;
              choices.push({ label, value });
            });
          return { ...field, choices: choices, disabled: false };
        }
        if (isPartialTransfer) {
          if (field.name === 'class_ratio' && targetClassRatio) {
            return { ...field, choices: targetClassRatio };
          }
          if (field.name === 'to_enrollment_type' && targetEnrollmentType) {
            return { ...field, choices: targetEnrollmentType };
          }
        }
        return field;
      });

    setFields(newFields);
  }

  const onOptionChange = (key, value) => {
    let newForm = { ...form };

    if (value?.target?.name === 'classes_to_transfer') {
      const numVal = Number(key);
      if (key !== '' && (!Number.isInteger(numVal) || numVal <= 0)) return;
      if (key !== '' && pendingClasses && numVal > pendingClasses) {
        setError(`Cannot transfer more than ${pendingClasses} classes`);
        return;
      }
      newForm['classes_to_transfer'] = key;

      setForm(newForm);
      setError(null);
    } else {
      if (key === 'students' && !isPartialTransfer) {
        newForm = { ...form, student_course: '' };
        const studentCourses = students && students[value].courses;
        const newFields =
          fields &&
          fields.map(field => {
            if (field.name === 'student_course') {
              const choices = [];
              studentCourses &&
                studentCourses.forEach(course => {
                  const { id: courseId, course_detail: courseDetail } = course || {};
                  const { name: courseName } = courseDetail || {};
                  const label = courseName;
                  const value = courseId;
                  choices.push({ label, value });
                });
              return { ...field, choices: choices, disabled: false };
            }
            return field;
          });
        setFields(newFields);
      }

      newForm[key] = value;

      setForm(newForm);
      setError(null);
    }
  };

  const onSubmitClick = () => {
    setShowConfirmation(true);
  };

  const onConfirm = () => {
    const {
      students: toStudentId,
      student_course: toStudentCourseId,
      online_band_list: onlineBandId,
      pending_classes: pendingClasses,
      course,
      classes_to_transfer: classesToTransfer,
      teacher_id: teacherId,
      to_enrollment_type: toEnrollmentType,
      class_ratio: classRatio,
    } = form || {};
    const patchData = {
      to_student_id: toStudentId,
      to_student_course_id: toStudentCourseId,
      online_band_id: onlineBandId,
      pending_classes: pendingClasses,
      course,
      classes_to_transfer: classesToTransfer,
      teacher_id: teacherId,
      to_enrollment_type: toEnrollmentType,
      to_class_ratio: classRatio,
    };
    setLoading(true);
    setShowConfirmation(false);
    actions.patchStudentCourseClassesTranferDetails(patchId, studentCourseId, patchData);
  };

  const onCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const isButtonDisabled = checkButtonStatus(form);
  const buttonLabel = 'Submit';

  return (
    <Wrapper>
      {renderTeachersList()}
      {fields &&
        Array.isArray(fields) &&
        fields?.map(field =>
          renderFormField({
            ...field,
            value: form[field.name],
            onChange: onOptionChange,
            onInputChange: onTeacherSearch,
          }),
        )}
      {error && (
        <Message width="100%" error>
          {error}
        </Message>
      )}
      {success ? (
        <Message width="100%">
          {patchStudentCourseClassesTransferData?.message ||
            'Classes transferred successfully'}
        </Message>
      ) : (
        <>
          <Separator transparent height="20" />
          {showConfirmation ? (
            <>
              <ConfirmationText>
                Are you sure you want to transfer{' '}
                <strong>{form.classes_to_transfer || pendingClasses}</strong> class
                {Number(form.classes_to_transfer || pendingClasses) !== 1 ? 'es' : ''}?
                This action cannot be undone.
              </ConfirmationText>
              <ButtonWrapper>
                {loading ? (
                  <BallLoader />
                ) : (
                  <>
                    <Button
                      align="center"
                      label="Cancel"
                      onClick={onCancelConfirmation}
                      secondary
                    />
                    <Separator transparent width="10" />
                    <Button align="center" label="Confirm" onClick={onConfirm} />
                  </>
                )}
              </ButtonWrapper>
            </>
          ) : (
            <ButtonWrapper>
              {loading ? (
                <BallLoader />
              ) : (
                <Button
                  align="center"
                  label={buttonLabel}
                  onClick={onSubmitClick}
                  disabled={isButtonDisabled}
                />
              )}
            </ButtonWrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

export default connect(
  ({ studentCourseClassesTransfer, teacherList }) => {
    return { studentCourseClassesTransfer, teacherList };
  },
  dispatch => ({
    actions: bindActionCreators(
      {
        getStudentCourseClassesTranferDetails,
        patchStudentCourseClassesTranferDetails,
        closeModal,
        getTeacherList,
      },
      dispatch,
    ),
  }),
)(StudentTransferClassesForm);
